import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import capitalize from 'lodash/capitalize';

import AGChart from 'components/shared/AGChart/AGChart';
import WidgetHeader from 'components/shared/CustomizableBoard/widgets/components/WidgetHeader';
import Icon from 'components/shared/Icon/Icon';

import {
  mappedColors,
  mappedCountField,
  mappedLabelValueFields,
  mappedNavigateRoute,
  mappedWidgetName
} from './lib/helpers';

const WidgetRenderer = (props) => {
  const { item, overview } = props;

  const navigate = useNavigate();

  const labels = mappedLabelValueFields[item?.widget] || {};

  const countValues = overview?.count?.[mappedCountField[item?.widget]] || {};

  const data = Object.entries(labels).map(([key, field]) => {
    return { label: capitalize(key), value: countValues?.[field] || 0 };
  });
  const all = data.reduce((acc, curr) => acc + (curr?.value || 0), 0);

  const percentage = (value) => {
    return (Math.floor((value / all) * 100) || 0) + '%';
  };

  const chartOptions = useMemo(() => {
    return {
      background: { visible: false },
      series: [
        {
          type: 'pie',
          angleKey: 'value',
          labelKey: 'label',
          fills: mappedColors?.[item?.widget] || ['#13B9FF', '#8191E8'],
          strokes: ['#fff'],
          innerRadiusRatio: 0.8, // makes it doughnut-like
          data,
          tooltip: {
            enabled: true,
            renderer: ({ datum }) => ({
              content: `${datum.label}: ${datum.value} (${percentage(datum.value)})`
            })
          }
        }
      ]
    };
  }, [data]);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <WidgetHeader icon="chart-mono" iconColor="primary" hasPlus={false} {...props}>
        <div
          onClick={() => navigate(mappedNavigateRoute[item?.widget])}
          className="mx-1 cursor-pointer rounded-lg bg-neutral-50 !px-2 !py-2">
          <Icon className="cursor-pointer" icon="link-linear" />
        </div>
      </WidgetHeader>

      <div className="flex flex-1 items-center justify-between overflow-hidden">
        <div className="flex flex-1 flex-col justify-center gap-2 !pl-10">
          <table className="inline-block text-left">
            {data.map(({ label, value }, index) => (
              <tr key={index}>
                <td className="!py-[5px] !pr-2">
                  <div
                    style={{ backgroundColor: mappedColors?.[item?.widget]?.[index] }}
                    className="h-4 w-4 rounded-full"
                  />
                </td>
                <td className="!pr-3">
                  <p className="font-medium">{label}</p>
                </td>
                <td className="!pr-3">
                  <p className="text-neutral-600">{percentage(value)}</p>
                </td>
                <td>
                  <p className="font-semibold text-neutral-900">{value}</p>
                </td>
              </tr>
            ))}
          </table>
        </div>

        <div className="!aspect-1 relative h-full !max-h-[360px] w-full !max-w-[360px]">
          <div className="pointer-events-none absolute left-1/2 top-1/2 flex translate-x-[-50%] translate-y-[-50%] flex-col justify-center text-center">
            <p className="text-2xl font-semibold text-primary-900">{all}</p>
            <p className="-mt-1 text-neutral-600">{mappedWidgetName?.[item.widget]}</p>
          </div>

          <AGChart options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default WidgetRenderer;
